import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { getOnboardingState, saveOnboardingState } from '@/lib/onboarding-store';
import { isQuickLoginEnabled, rememberAccount, accountFromSession, clearSessionParked } from '@/lib/quick-login';
import { MARK_ARC_FILLED_D, MARK_DOT, MARK_VIEWBOX, BRAND } from '@/lib/brand-assets';

type Status =
  | { kind: 'loading' }
  | { kind: 'refused'; intent: MsIntent }
  | { kind: 'error'; message: string };

type MsIntent = 'school' | 'teacher';

function readIntent(): MsIntent {
  const params = new URLSearchParams(window.location.search);
  return params.get('intent') === 'school' ? 'school' : 'teacher';
}

/**
 * OAuth landing page. The Supabase client processes the redirect URL on load
 * (detectSessionInUrl) and establishes the session; here we resolve the
 * signed-in user's role from user_metadata and route them into the matching
 * portal — mirroring exactly what the email/password login pages do.
 *
 * Link-only: a Microsoft account with no CoverMyClass role is REFUSED (signed
 * out with a clear "ask your admin" message), never treated as a new account.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  useEffect(() => {
    let settled = false;
    const intent = readIntent();

    // Provider-side failure (consent declined, config error, etc.)
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const providerError =
      search.get('error_description') || search.get('error') ||
      hash.get('error_description') || hash.get('error');
    if (providerError) {
      setStatus({ kind: 'error', message: decodeURIComponent(providerError.replace(/\+/g, ' ')) });
      return;
    }

    const routeFor = async (session: Session) => {
      if (settled) return;
      settled = true;

      const user = session.user;
      const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
      const role = meta.role as string | undefined;

      // Active sign-in — this session is no longer a "parked" post-logout one.
      if (role === 'teacher' || role === 'school_admin') clearSessionParked();

      if (role === 'teacher') {
        sessionStorage.setItem('cmc_teacher_token', session.access_token);
        sessionStorage.setItem('cmc_teacher_id', user.id);
        // Trusted-device quick login: if the teacher has it on, remember this
        // Microsoft account so it appears as a one-tap card next time.
        if (isQuickLoginEnabled()) {
          const acct = accountFromSession(session, 'microsoft');
          if (acct) rememberAccount(acct);
        }
        const metaName = (meta.full_name as string | undefined)
          ?? (meta.coverTeacherName as string | undefined)
          ?? `${meta.firstName ?? ''} ${meta.lastName ?? ''}`.trim();
        const name = metaName || (user.email ?? '').split('@')[0];
        const state = getOnboardingState();
        saveOnboardingState({
          ...state,
          teacherName: name,
          teacherEmail: user.email ?? '',
          teacherDepartment: (meta.department as string | undefined) ?? '',
          isComplete: true,
          userType: 'teacher',
        });
        navigate('/');
        return;
      }

      if (role === 'school_admin') {
        sessionStorage.setItem('cmc_school_token', session.access_token);
        // Trusted-device quick login: remember this admin account when the
        // feature is on, so it appears as a one-tap card next time.
        if (isQuickLoginEnabled()) {
          const acct = accountFromSession(session, 'microsoft');
          if (acct) rememberAccount(acct);
        }
        // Secondary contacts carry the primary admin's id in schoolId.
        const metaSchoolId = meta.schoolId as string | undefined;
        const effectiveSchoolId = (metaSchoolId && metaSchoolId !== user.id)
          ? metaSchoolId
          : user.id;
        const state = getOnboardingState();
        saveOnboardingState({ ...state, isComplete: true, userType: 'school', supabaseUserId: effectiveSchoolId });
        navigate('/');
        return;
      }

      // No CoverMyClass role → this Microsoft account isn't provisioned.
      // Sign the orphan session out and refuse.
      await supabase.auth.signOut();
      setStatus({ kind: 'refused', intent });
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) void routeFor(session);
    });

    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) void routeFor(data.session);
    });

    // Fallback: if the session never materialises, don't hang forever.
    const timer = setTimeout(async () => {
      if (settled) return;
      const { data } = await supabase.auth.getSession();
      if (data.session) { void routeFor(data.session); return; }
      settled = true;
      setStatus({ kind: 'error', message: 'Sign-in timed out before it completed.' });
    }, 6000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4 relative">
      <div className="absolute inset-0 paper-dots-dark pointer-events-none" aria-hidden="true" />
      <div className="w-full max-w-md relative">
        <div className="bg-card rounded-lg shadow-2xl p-8 text-center">
          <svg viewBox={MARK_VIEWBOX} className="w-14 h-14 mx-auto mb-4" aria-hidden="true">
            <path d={MARK_ARC_FILLED_D} fill={BRAND.navy700} />
            <circle cx={MARK_DOT.cx} cy={MARK_DOT.cy} r={MARK_DOT.r} fill={BRAND.navy500} />
          </svg>

          {status.kind === 'loading' && (
            <>
              <h1 className="font-display text-2xl font-bold mb-2">Signing you in…</h1>
              <p className="text-muted-foreground">Confirming your Microsoft account with CoverMyClass.</p>
            </>
          )}

          {status.kind === 'refused' && (
            <>
              <h1 className="font-display text-2xl font-bold mb-2">No CoverMyClass account yet</h1>
              <p className="text-muted-foreground mb-6">
                That Microsoft account isn't linked to CoverMyClass.{' '}
                {status.intent === 'teacher'
                  ? 'Ask your school admin to invite you. The invitation links your account.'
                  : 'Sign in with the email you registered your school with, or create a school account.'}
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate(status.intent === 'teacher' ? '/teacher/login' : '/school/login')}
                  className="w-full h-11 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
                >
                  Back to sign in
                </button>
              </div>
            </>
          )}

          {status.kind === 'error' && (
            <>
              <h1 className="font-display text-2xl font-bold mb-2">Sign-in didn't complete</h1>
              <p className="text-muted-foreground mb-6">{status.message}</p>
              <button
                onClick={() => navigate('/school/login')}
                className="w-full h-11 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
              >
                Back to sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
